import { MapInteraction } from 'react-map-interaction';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';

import Content from '../../components/Content';
import Empty from '../../components/Empty';
import Header from '../../components/Header';
import Loader from '../../components/Loader';
import styles from './map.scss';

const propTypes = {
  onRequestOpenFn: PropTypes.func.isRequired,
  event: PropTypes.shape({
    venue: PropTypes.object,
  }),
  loading: PropTypes.bool,
  match: PropTypes.shape({
    params: PropTypes.shape({
      node: PropTypes.string,
    }),
  }),
  history: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
  }).isRequired,
};

const Shape = (props) => {
  const {
    type,
    path,
    ...restProps
  } = props;
  if (type === 'path') {
    return <path d={path} {...restProps} />;
  } if (type === 'polyline') {
    return <polyline points={path} {...restProps} />;
  } if (type === 'polygon') {
    return <polygon points={path} {...restProps} />;
  }
  return null;
};
Shape.propTypes = { type: PropTypes.string, path: PropTypes.string };

const flattenVenue = (venue) => {
  const floors = (venue && venue.nodes) || [];
  const areas = (floors && floors.reduce((acc, floor) => acc.concat(floor.nodes), [])) || [];
  const subareas = (areas && areas.reduce((acc, area) => acc.concat(area.nodes), [])) || [];
  return [
    ...floors,
    ...areas,
    ...subareas,
  ];
};

class Map extends React.Component {
  constructor() {
    super();
    this.state = {
      activeArea: {},
    };
  }

  componentDidMount() {
    const { match, event } = this.props;
    const nodeId = match && match.params && match.params.node;
    const activeArea = event && flattenVenue(event.venue).find((node) => node.id === nodeId);

    if (activeArea) {
      this.setActiveArea(activeArea);
    }
  }

  setActiveArea = (area) => {
    const { activeArea } = this.state;
    if (area.id === activeArea.id) {
      this.setState({
        activeArea: {},
      });
    } else {
      this.setState({
        activeArea: area,
      });
    }
  }

  render() {
    const {
      onRequestOpenFn,
      event,
      loading,
      history,
    } = this.props;
    const { activeArea } = this.state;
    const venue = event && event.venue;

    return (
      <>
        <Header
          onMenuOpen={onRequestOpenFn}
          onBack={activeArea.id ? () => history.goBack() : undefined}
        >
          Map
        </Header>
        <Content>
          <Loader loading={loading} />
          <Empty
            empty={!loading && (!venue || !venue.nodes)}
            title="There is no map for this event yet."
            message="We probably haven't uploaded the map yet, but check back later to see if it's up!"
          />
          { venue && venue.nodes
            && (
              <>
                <div className={styles.map}>
                  <MapInteraction
                    scale={1}
                    minScale={1}
                    maxScale={5}
                    showControls
                    plusBtnContents={<i className="far fa-search-plus" />}
                    minusBtnContents={<i className="far fa-search-minus" />}
                    controlsClass={styles.controls}
                  >
                    {
                    ({ translation, scale }) => (
                      <div className={styles.svgContainer}>
                        <div
                          className={styles.svgScaler}
                        >
                          <svg
                            xmlnsXlink="http://www.w3.org/2000/svg"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox={`0 0 ${venue.width} ${venue.height}`}
                            version="1.1"
                            role="img"
                            className={styles.mapSvg}
                            style={{
                              transform: `translate(${translation.x}px, ${translation.y}px) scale(${scale})`,
                            }}
                          >
                            { venue.nodes.map((floor) => (
                              <g key={floor.id}>
                                <Shape
                                  type={floor.type}
                                  path={floor.path}
                                  className={styles.floor}
                                />
                              </g>
                            ))}
                            { venue.nodes.map((floor) => (
                              floor.nodes.map((area) => (
                                <g
                                  key={area.id}
                                  className={classNames(
                                    styles.area,
                                    { [`${styles.active}`]: area.id === activeArea.id || area.id === activeArea.parent_id },
                                  )}
                                >
                                  <Shape
                                    type={area.type}
                                    path={area.path}
                                  />
                                  { area.nodes?.length > 0 && area.nodes.map((subarea) => (
                                    <g
                                      key={subarea.id}
                                      className={classNames(
                                        styles.subarea,
                                        { [`${styles.active}`]: subarea.id === activeArea.id },
                                      )}
                                    >
                                      <Shape
                                        type={subarea.type}
                                        path={subarea.path}
                                      />
                                    </g>
                                  ))}
                                </g>
                              ))
                            ))}
                            { venue.nodes.map((floor) => (
                              <g
                                key={floor.id}
                                className={styles.decor}
                                /* eslint-disable react/no-danger */
                                dangerouslySetInnerHTML={{ __html: floor.decor }}
                                /* eslint-enable */
                              />
                            ))}
                          </svg>
                        </div>
                      </div>
                    )
                  }
                  </MapInteraction>
                </div>
                <div className={styles.info}>
                  { activeArea.id
                    && (
                    <div className={styles.selected}>
                      {activeArea.name}
                    </div>
                    )}
                  <div className={styles.zoom}>
                    You can zoom by pinching and dragging on the map.
                  </div>
                </div>
              </>
            )}
        </Content>
      </>
    );
  }
}

Map.propTypes = propTypes;

function mapStateToProps(state) {
  return {
    loading: state.getIn(['event', 'loading']),
    event: state.getIn(['event', 'data']),
  };
}

export default connect(mapStateToProps)(Map);
